import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  disciplinaryPointsForTeamInMatch,
  isMatchLive,
  liveMinuteLabel,
  matchPausedLabel,
  orderGroupStandings,
  qualifyingThirdPlaceIds,
  rankThirdPlacedTeams,
  type H2HMatch,
  type MatchPublic,
  type TeamPublic,
} from '@mondy/shared';
import { TeamFlag } from '@/components/TeamFlag';

export interface StandingRow {
  team: TeamPublic;
  p: number;
  w: number;
  d: number;
  l: number;
  pts: number;
  gf: number;
  ga: number;
  // Overall fair-play penalty total — the conduct tiebreaker. Not displayed.
  disciplinaryPoints: number;
  // Per-field live impact. Non-zero means the stat contains contribution
  // from an in-flight match and should be colored.
  pLive: number;
  wLive: number;
  dLive: number;
  lLive: number;
  ptsLive: number;
  // Live goal contribution: gf/ga added by an in-flight match. Goals only
  // shift when goals are actually scored, so these (not pLive) drive GF/GA/GD
  // coloring — a 0-0 live game leaves all three uncolored.
  gfLive: number;
  gaLive: number;
  // Which concurrent live match in the group this team is in (-1 = none).
  // Two teams sharing an index are playing each other right now.
  liveMatch: number;
  // True for a 3rd-placed team currently among the 8 best third places (so
  // provisionally through to the round of 32). Set across groups in buildGroups.
  thirdPlaceQualifying: boolean;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

function emptyRow(team: TeamPublic): StandingRow {
  return {
    team,
    p: 0,
    w: 0,
    d: 0,
    l: 0,
    pts: 0,
    gf: 0,
    ga: 0,
    disciplinaryPoints: 0,
    pLive: 0,
    wLive: 0,
    dLive: 0,
    lLive: 0,
    ptsLive: 0,
    gfLive: 0,
    gaLive: 0,
    liveMatch: -1,
    thirdPlaceQualifying: false,
  };
}

function applyMatch(
  row: StandingRow,
  selfGoals: number,
  oppGoals: number,
  live: boolean,
) {
  row.p += 1;
  row.gf += selfGoals;
  row.ga += oppGoals;
  if (live) {
    row.pLive += 1;
    row.gfLive += selfGoals;
    row.gaLive += oppGoals;
  }
  if (selfGoals > oppGoals) {
    row.w += 1;
    row.pts += 3;
    if (live) {
      row.wLive += 1;
      row.ptsLive += 3;
    }
  } else if (selfGoals < oppGoals) {
    row.l += 1;
    if (live) row.lLive += 1;
  } else {
    row.d += 1;
    row.pts += 1;
    if (live) {
      row.dLive += 1;
      row.ptsLive += 1;
    }
  }
}

function computeStandings(
  teams: TeamPublic[],
  matches: MatchPublic[],
): StandingRow[] {
  const rows = new Map<string, StandingRow>();
  for (const t of teams) rows.set(t.id, emptyRow(t));

  // Counts concurrent live matches so the two teams in each get a shared,
  // distinct tint. A team plays one match at a time, so this is unambiguous.
  let liveCount = 0;
  const h2hMatches: H2HMatch[] = [];
  for (const m of matches) {
    if (!m.team1 || !m.team2) continue;
    const r1 = rows.get(m.team1.id);
    const r2 = rows.get(m.team2.id);
    if (!r1 || !r2) continue;

    const live = isMatchLive(m);
    const finished = m.status === 'finished';
    if (!live && !finished) continue;

    if (live) {
      r1.liveMatch = liveCount;
      r2.liveMatch = liveCount;
      liveCount += 1;
    }

    // Group stage uses only the FT score. Default to 0-0 for a live match
    // that hasn't recorded a score yet (first whistle, no goals).
    const ft = m.result?.fullTime ?? { team1: 0, team2: 0 };
    applyMatch(r1, ft.team1, ft.team2, live);
    applyMatch(r2, ft.team2, ft.team1, live);

    // Head-to-head + conduct draw from the same finished/live matches.
    h2hMatches.push({
      team1Id: m.team1.id,
      team2Id: m.team2.id,
      team1Goals: ft.team1,
      team2Goals: ft.team2,
    });
    r1.disciplinaryPoints += disciplinaryPointsForTeamInMatch(
      m.events.filter((e) => e.side === 'team1'),
    );
    r2.disciplinaryPoints += disciplinaryPointsForTeamInMatch(
      m.events.filter((e) => e.side === 'team2'),
    );
  }

  return orderGroupStandings(Array.from(rows.values()), h2hMatches);
}

export interface Group {
  group: string;
  rows: StandingRow[];
}

export function buildGroups(
  teams: TeamPublic[],
  matches: MatchPublic[],
): Group[] {
  const byGroup = new Map<string, TeamPublic[]>();
  for (const t of teams) {
    const k = t.group ?? '—';
    const arr = byGroup.get(k) ?? [];
    arr.push(t);
    byGroup.set(k, arr);
  }
  const matchesByGroup = new Map<string, MatchPublic[]>();
  for (const m of matches) {
    if (m.stage !== 'group') continue;
    const k = m.group ?? '—';
    const arr = matchesByGroup.get(k) ?? [];
    arr.push(m);
    matchesByGroup.set(k, arr);
  }
  const groups = Array.from(byGroup.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, items]) => ({
      group,
      rows: computeStandings(items, matchesByGroup.get(group) ?? []),
    }));
  // The 8 best third-placed teams across all groups advance to the round of 32.
  // Flag each qualifying group's 3rd row so the table can outline it (live
  // results are already in the rows, so this tracks the standings as they move).
  const qualifying = qualifyingThirdPlaceIds(groups);
  for (const g of groups) {
    const third = g.rows[2];
    if (third) third.thirdPlaceQualifying = qualifying.has(third.team.id);
  }
  return groups;
}

// Coloring for a stat value affected by an in-flight match. Any live impact
// is shown in green regardless of outcome.
function liveColor(live: number): string | undefined {
  return live === 0 ? undefined : 'var(--turf)';
}

// Goal difference shown with an explicit sign for positives (+4, 0, -3).
function fmtGd(gd: number): string {
  return gd > 0 ? `+${gd}` : String(gd);
}

// One tint per concurrent live match in a group, so the two teams playing
// each other share a color and are told apart from the other live pair.
const LIVE_TINTS = [
  {
    bar: 'var(--turf)',
    bg: 'color-mix(in oklab, var(--turf) 10%, transparent)',
    dot: 'var(--turf)',
    dotSoft: 'var(--turf-soft)',
  },
  {
    bar: 'var(--turf-deep)',
    bg: 'color-mix(in oklab, var(--turf-deep) 18%, transparent)',
    dot: 'var(--turf-deep)',
    dotSoft: 'var(--turf-deep-soft)',
  },
];

const STANDINGS_GRID =
  'minmax(0, 1fr) 18px 18px 18px 18px 20px 20px 26px 24px';

// Third-place rows prefix a rank gutter and add a "G" (group) column before the
// stat columns, since these teams span all 12 groups (unlike a single group
// card). It no longer shares STANDINGS_GRID's column track as a result.
const THIRD_PLACE_GRID =
  '20px minmax(0, 1fr) 18px 18px 18px 18px 18px 20px 20px 26px 24px';

const statCellStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: 12,
  letterSpacing: '0.02em',
};

function StatHeader({ label }: { label: string }) {
  return (
    <div
      className="mono"
      style={{
        ...statCellStyle,
        fontSize: 9,
        letterSpacing: '0.12em',
        color: 'var(--fg-muted)',
        textTransform: 'uppercase',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
  );
}

function StatCell({
  value,
  color,
  strong,
}: {
  value: number | string;
  color?: string;
  strong?: boolean;
}) {
  return (
    <div
      className="mono tabular"
      style={{
        ...statCellStyle,
        fontWeight: strong ? 700 : 500,
        color: color ?? 'var(--fg)',
      }}
    >
      {value}
    </div>
  );
}

function StandingsTable({ rows }: { rows: StandingRow[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: STANDINGS_GRID,
          alignItems: 'center',
          columnGap: 4,
          padding: '4px 0 6px',
          borderBottom: '1px solid var(--line)',
        }}
      >
        <div />
        <StatHeader label="P" />
        <StatHeader label="W" />
        <StatHeader label="D" />
        <StatHeader label="L" />
        <StatHeader label="GF" />
        <StatHeader label="GA" />
        <StatHeader label="GD" />
        <StatHeader label="PTS" />
      </div>
      {rows.map((r, i) => {
        // Teams in an in-flight match get a tint; the two teams playing each
        // other share one, so concurrent matches in a group stay distinct.
        const tint =
          r.liveMatch >= 0 ? LIVE_TINTS[r.liveMatch % LIVE_TINTS.length] : null;
        // A 3rd-placed team among the best-8 third places is marked with a red
        // "Q" badge next to its code (see QBadge). Only a live tint still bleeds
        // into the card's 18px gutter so the stat columns stay aligned.
        const qualifying = r.thirdPlaceQualifying;
        const bleed = Boolean(tint);
        const boxShadow = tint ? `inset 3px 0 0 ${tint.bar}` : undefined;
        return (
        <Link
          key={r.team.id}
          to={`/teams/${r.team.id}`}
          className="standings-row"
          style={{
            display: 'grid',
            gridTemplateColumns: STANDINGS_GRID,
            alignItems: 'center',
            columnGap: 4,
            padding: bleed ? '7px 18px' : '7px 0',
            margin: bleed ? '0 -18px' : undefined,
            borderTop: i === 0 ? 'none' : '1px dashed var(--line)',
            background: tint ? tint.bg : undefined,
            boxShadow,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0,
            }}
          >
            <TeamFlag
              code={r.team.fifaCode}
              url={r.team.flagUrl}
              name={r.team.name}
              size="xs"
            />
            <span
              style={{
                minWidth: 0,
                fontSize: 13,
                color: 'var(--fg)',
              }}
            >
              {r.team.name}
            </span>
            {/* Q sits immediately after the team name. */}
            {qualifying && <QBadge />}
            {tint && (
              <span
                className="standings-live-dot"
                style={{
                  background: tint.dot,
                  boxShadow: `0 0 0 4px ${tint.dotSoft}`,
                }}
              />
            )}
          </div>
          <StatCell value={r.p} color={liveColor(r.pLive)} />
          <StatCell value={r.w} color={liveColor(r.wLive)} />
          <StatCell value={r.d} color={liveColor(r.dLive)} />
          <StatCell value={r.l} color={liveColor(r.lLive)} />
          <StatCell value={r.gf} color={liveColor(r.gfLive)} />
          <StatCell value={r.ga} color={liveColor(r.gaLive)} />
          <StatCell value={fmtGd(r.gf - r.ga)} color={liveColor(r.gfLive - r.gaLive)} />
          <StatCell value={r.pts} color={liveColor(r.ptsLive)} strong />
        </Link>
        );
      })}
    </div>
  );
}

export function GroupCard({ group, rows }: Group) {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <header style={{ marginBottom: 8 }}>
        <div
          className="bebas"
          style={{
            fontSize: 28,
            letterSpacing: '0.04em',
          }}
        >
          Group {group}
        </div>
      </header>
      <StandingsTable rows={rows} />
    </section>
  );
}

// Small red "Q" disc marking a 3rd-placed team currently among the 8 best
// third places (provisionally through to the round of 32). Used both in the
// group standings rows and in the third-place ranking list.
export function QBadge() {
  return (
    <span
      title="One of the 8 best third-placed teams advancing to the round of 32"
      aria-label="Qualifying third-placed team"
      style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: 'var(--danger)',
        color: '#000',
        fontSize: 10,
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      Q
    </span>
  );
}

// Glossary for the standings stat columns. Rendered on the Teams page beneath
// the Q caption — kept separate from ThirdPlaceLegend (which is also reused on
// the team-detail page, where there's no third-place ranking or "G" column).
const STAT_GLOSSARY: [string, string][] = [
  ['G', 'group'],
  ['P', 'played'],
  ['W', 'wins'],
  ['D', 'draws'],
  ['L', 'losses'],
  ['GF', 'goals for'],
  ['GA', 'goals against'],
  ['GD', 'goal difference'],
  ['PTS', 'points'],
];

export function StandingsGlossary() {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px 14px',
        fontSize: 11,
        color: 'var(--fg-muted)',
      }}
    >
      {STAT_GLOSSARY.map(([abbr, meaning]) => (
        <span
          key={abbr}
          style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}
        >
          <span
            className="mono"
            style={{ color: 'var(--fg)', fontWeight: 700, letterSpacing: '0.04em' }}
          >
            {abbr}
          </span>
          {meaning}
        </span>
      ))}
    </div>
  );
}

// Caption explaining the red "Q" badge on qualifying third-placed rows. Shown
// on a team page whose group's third-placed team is currently qualifying.
export function ThirdPlaceLegend() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: 'var(--fg-muted)',
      }}
    >
      <QBadge />
      = one of the 8 best third-placed teams advancing to the round of 32.
    </div>
  );
}

const THIRD_PLACE_OPEN_KEY = 'mondy_thirdplace_open';

function readThirdPlaceOpenPref(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(THIRD_PLACE_OPEN_KEY);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // localStorage may throw in private browsing / disabled storage modes —
    // fall through to the default.
  }
  return true; // default: expanded
}

// Collapsible ranking of every group's third-placed team, best-first, with a
// cut line after the 8 that provisionally advance. Open/closed state persists
// in localStorage. World Cup groups always have a third row once teams are
// seeded, so this lists all 12 thirds.
export function ThirdPlaceRanking({
  groups,
  matches,
}: {
  groups: Group[];
  matches: MatchPublic[];
}) {
  const [open, setOpen] = useState<boolean>(readThirdPlaceOpenPref);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(THIRD_PLACE_OPEN_KEY, open ? '1' : '0');
    } catch {
      // Ignore persistence failures (private mode, quota, etc.).
    }
  }, [open]);

  const ranked = useMemo(
    () =>
      rankThirdPlacedTeams(
        groups
          .map((g) => g.rows[2])
          .filter((r): r is StandingRow => Boolean(r)),
      ),
    [groups],
  );

  // Live group matches keyed by each participant so a third-placed team that's
  // currently playing can show its opponent, score, and minute beneath its row.
  const liveByTeam = useMemo(() => {
    const map = new Map<string, MatchPublic>();
    for (const m of matches) {
      if (!isMatchLive(m) || !m.team1 || !m.team2) continue;
      map.set(m.team1.id, m);
      map.set(m.team2.id, m);
    }
    return map;
  }, [matches]);

  // Narrow viewports swap the full team name for its 3-letter FIFA code so the
  // 11-column ranking grid doesn't crowd. 820px matches the dominant mobile
  // breakpoint used throughout index.css.
  const isNarrow = useMediaQuery('(max-width: 820px)');

  if (ranked.length === 0) return null;

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '12px 16px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            className="bebas"
            style={{ fontSize: 22, letterSpacing: '0.04em' }}
          >
            Third-place ranking
          </span>
          <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
            best 8 advance
          </span>
        </span>
        <span
          aria-hidden
          style={{
            color: 'var(--fg-muted)',
            fontSize: 12,
            transform: open ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.15s',
          }}
        >
          ▼
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 16px 12px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: THIRD_PLACE_GRID,
              alignItems: 'center',
              columnGap: 4,
              padding: '4px 0 6px',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <div />
            <div />
            <StatHeader label="G" />
            <StatHeader label="P" />
            <StatHeader label="W" />
            <StatHeader label="D" />
            <StatHeader label="L" />
            <StatHeader label="GF" />
            <StatHeader label="GA" />
            <StatHeader label="GD" />
            <StatHeader label="PTS" />
          </div>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {ranked.map((r, i) => {
              const cut = i === 8 && ranked.length > 8;
              const live = liveByTeam.get(r.team.id);
              return (
                <li key={r.team.id}>
                  {cut && (
                    <div
                      aria-hidden
                      style={{
                        borderTop: '2px solid var(--hot)',
                        margin: '6px 0',
                      }}
                    />
                  )}
                  <Link
                    to={`/teams/${r.team.id}`}
                    className="standings-row"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      padding: '7px 0',
                      borderTop:
                        i === 0 || cut ? 'none' : '1px dashed var(--line)',
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: THIRD_PLACE_GRID,
                        alignItems: 'center',
                        columnGap: 4,
                      }}
                    >
                      <span
                        className="mono tabular"
                        style={{
                          fontSize: 11,
                          color: 'var(--fg-muted)',
                          textAlign: 'right',
                        }}
                      >
                        {i + 1}
                      </span>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          minWidth: 0,
                        }}
                      >
                        <TeamFlag
                          code={r.team.fifaCode}
                          url={r.team.flagUrl}
                          name={r.team.name}
                          size="xs"
                        />
                        <span
                          style={{
                            minWidth: 0,
                            fontSize: 13,
                          }}
                        >
                          {isNarrow ? (r.team.fifaCode || r.team.name) : r.team.name}
                        </span>
                      </div>
                      <StatCell value={r.team.group ?? '—'} color="var(--fg-muted)" />
                      <StatCell value={r.p} color={liveColor(r.pLive)} />
                      <StatCell value={r.w} color={liveColor(r.wLive)} />
                      <StatCell value={r.d} color={liveColor(r.dLive)} />
                      <StatCell value={r.l} color={liveColor(r.lLive)} />
                      <StatCell value={r.gf} color={liveColor(r.gfLive)} />
                      <StatCell value={r.ga} color={liveColor(r.gaLive)} />
                      <StatCell
                        value={fmtGd(r.gf - r.ga)}
                        color={liveColor(r.gfLive - r.gaLive)}
                      />
                      <StatCell
                        value={r.pts}
                        color={liveColor(r.ptsLive)}
                        strong
                      />
                    </div>
                    {live && <LiveLine match={live} teamId={r.team.id} />}
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
}

// One-line live readout shown under a third-placed team that's in an in-flight
// group match: "67' vs CAN 1–2" in turf, echoing the group table's live tint.
function LiveLine({ match, teamId }: { match: MatchPublic; teamId: string }) {
  const isTeam1 = match.team1?.id === teamId;
  const opp = isTeam1 ? match.team2 : match.team1;
  if (!opp) return null;
  const ft = match.result?.fullTime ?? { team1: 0, team2: 0 };
  const self = isTeam1 ? ft.team1 : ft.team2;
  const against = isTeam1 ? ft.team2 : ft.team1;
  const clock =
    match.status === 'paused'
      ? matchPausedLabel(match.minute)
      : liveMinuteLabel(match.minute, match.addedTime);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginLeft: 24,
        fontSize: 11,
        color: 'var(--turf)',
      }}
    >
      <span
        className="standings-live-dot"
        style={{ background: 'var(--turf)', boxShadow: '0 0 0 4px var(--turf-soft)' }}
      />
      {clock && <span style={{ fontWeight: 700 }}>{clock}</span>}
      <span style={{ color: 'var(--fg-muted)' }}>vs</span>
      <TeamFlag code={opp.fifaCode} url={opp.flagUrl} name={opp.name} size="xs" />
      <span className="mono" style={{ letterSpacing: '0.05em' }}>
        {opp.fifaCode}
      </span>
      <span
        className="mono tabular"
        style={{ fontWeight: 700, color: 'var(--fg)' }}
      >
        {self}–{against}
      </span>
    </div>
  );
}
